import { Routes } from '@angular/router';
import { AccessDeniedComponent } from '@components/ui/errors/access-denied/access-denied.component';
import { NotFoundComponent } from '@components/ui/errors/not-found/not-found.component';
import { AuthChildGuard, authGuard } from '@core/guards/auth.guard';
import { PageComponent } from '@pages/page.component';
import { ReportManagementComponent } from '@pages/report-management/report-management.component';
import { StatisticManagementComponent } from '@pages/statistic-management/statistic-management';
import { SystemSettingComponent } from '@pages/system-setting/system-setting.component';
import { UserManagementComponent } from '@pages/user-management/user-management.component';
import { addItineraryResolver } from '@resolves/add-itinerary.resolver';
import { bookingDetailResolver } from '@resolves/booking-detail.resolve';
import { changeFlightResolver } from '@resolves/change-flight.resolver';
import { orderDetailResolver } from '@resolves/order-detail.resolve';

export const routes: Routes = [
    {
        path: '',
        component: PageComponent,
        canActivate: [authGuard],
        children: [
            {
                path: 'system-setting',
                redirectTo: '/system-setting/cheep-management',
                pathMatch: 'full',
            },
            { path: 'booking', redirectTo: '/booking/booking-manage', pathMatch: 'full' },
            { path: 'campaign', redirectTo: '/campaign/management', pathMatch: 'full' },
            { path: 'order', redirectTo: '/order/order-management', pathMatch: 'full' },
            {
                path: 'user-management',
                redirectTo: '/user-management/manage-account',
                pathMatch: 'full',
            },
            {
                path: 'campaign',
                data: { title: 'sidebar.campaign' },
                canActivate: [authGuard],
                canActivateChild: [AuthChildGuard],
                children: [
                    {
                        path: 'management',
                        loadComponent: () =>
                            import(
                                './pages/campaign-management/campaign/campaign.component'
                            ).then((v) => v.CampaignComponent),
                        data: { 
                            title: 'sidebar.campaignList',
                            controller: 'Campaign',
                            actionName: 'ManageCampaign',
                        },
                    },
                    {
                        path: 'carrier',
                        loadComponent: () =>
                            import(
                                './pages/campaign-management/promotion-carrier/promotion-carrier.component'
                            ).then((v) => v.PromotionCarrierComponent),
                        data: { 
                            title: 'sidebar.promotionCarrier',
                            controller: 'PromotionCarrier',
                            actionName: 'ManagePromotionCarrier',
                        },
                    },
                    {
                        path: 'email',
                        loadComponent: () =>
                            import(
                                './pages/campaign-management/promotion-email-receive/promotion-email-receive.component'
                            ).then((v) => v.PromotionEmailReceiveComponent),
                        data: { 
                            title: 'pages.promotionEmailReceive.title',
                            controller: 'EmailReceivePromotion',
                            actionName: 'ManageEmailReceivePromotion',
                        },
                    },
                ],
            },
            {
                path: 'booking',
                data: { title: 'sidebar.manageBooking' },
                canActivate: [authGuard],
                canActivateChild: [AuthChildGuard],
                children: [
                    {
                        path: 'booking-manage',
                        loadComponent: () =>
                            import(
                                './pages/booking/booking-management/booking-management.component'
                            ).then((v) => v.BookingManagementComponent),
                        data: { 
                            title: 'sidebar.manageBooking',
                            controller: 'Booking',
                            actionName: 'Booking-SearchListBooking',
                        },
                    },
                    {
                        path: 'booking-detail',
                        resolve: {
                            detail: bookingDetailResolver,
                        },
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        loadComponent: () =>
                            import('./pages/booking/booking-detail/booking-detail.component').then(
                                (v) => v.BookingDetailComponent
                            ),
                        data: { 
                            title: 'pages.booking.titlePageDetail',
                            controller: 'Booking',
                            actionName: 'Booking-SearchListBooking',
                        },
                    },
                    {
                        path: 'change-flight',
                        data: { 
                            title: 'common.action.btnChangeFlight',
                            controller: 'Booking',
                            actionName: 'Booking-SearchListBooking',
                        },
                        resolve: {
                            detail: changeFlightResolver,
                        },
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        loadComponent: () =>
                            import(
                                './pages/booking/booking-detail/change-flight/change-flight.component'
                            ).then((v) => v.ChangeFlightComponent),
                    },
                    {
                        path: 'add-itinerary',
                        data: { 
                            title: 'common.action.btnAddFlight',
                            controller: 'Booking',
                            actionName: 'Booking-SearchListBooking',
                        },
                        resolve: {
                            detail: addItineraryResolver,
                        },
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        loadComponent: () =>
                            import(
                                './pages/booking/booking-detail/add-itinerary/add-itinerary.component'
                            ).then((v) => v.AddItineraryComponent),
                    },
                ],
            },
            {
                path: 'order',
                data: { title: 'sidebar.orderManagement' },
                canActivate: [authGuard],
                canActivateChild: [AuthChildGuard],
                children: [
                    {
                        path: 'order-management',
                        loadComponent: () =>
                            import(
                                './pages/order/order-management/order-management.component'
                            ).then((v) => v.OrderManagementComponent),
                        data: { 
                            title: 'sidebar.orderManagement',
                            controller: 'Order',
                            actionName: 'Order-SearchListOrder',
                        },
                    },
                    {
                        path: 'order-detail',
                        resolve: {
                            detail: orderDetailResolver,
                        },
                        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
                        loadComponent: () =>
                            import('./pages/order/order-detail/order-detail.component').then(
                                (v) => v.OrderDetailComponent
                            ),
                        data: { 
                            title: 'pages.order.titleDetail',
                            controller: 'Order',
                            actionName: 'Order-SearchListOrder',
                        },
                    },
                ],
            },
            {
                path: 'user-management',
                component: UserManagementComponent,
                canActivate: [authGuard],
                canActivateChild: [AuthChildGuard],
                children: [
                    {
                        path: 'manage-group-role',
                        loadComponent: () =>
                            import(
                                './pages/user-management/manage-group-role/manage-group-role.component'
                            ).then((v) => v.ManageGroupRoleComponent),
                        data: {
                            title: 'pages.manageGroupRole.titlePage',
                            controller: 'Permission',
                            actionName: 'PermissionGroupRole',
                        },
                    },
                    {
                        path: 'manage-account',
                        loadComponent: () =>
                            import(
                                './pages/user-management/manage-account/manage-account.component'
                            ).then((v) => v.ManageAccountComponent),
                        data: {
                            title: 'pages.manageAccount.titlePage',
                            controller: 'User',
                            actionName: 'UserManage',
                        },
                    },
                    {
                        path: 'manage-permission',
                        loadComponent: () =>
                            import(
                                './pages/user-management/manage-permission/manage-permission.component'
                            ).then((v) => v.ManagePermissionComponent),
                        data: {
                            controller: 'Permission',
                            action: 'PermissionGroupRoleRole',
                            title: 'sidebar.permission',
                        },
                    },
                ],
            },
            {
                path: 'report',
                title: 'Report management',
                component: ReportManagementComponent,
                canActivate: [authGuard],
                canActivateChild: [AuthChildGuard],
                children: [
                    {
                        path: 'summary',
                        loadComponent: () =>
                            import(
                                './pages/report-management/summary-report/summary-report.component'
                            ).then((v) => v.SummaryReportComponent),
                        data: { 
                            title: 'sidebar.summaryReport',
                            controller: 'Report',
                            actionName: 'GeneralReport',
                        },
                    },
                ],
            },
            {
                path: 'statistic',
                title: 'Statistic management',
                component: StatisticManagementComponent,
                canActivate: [authGuard],
                canActivateChild: [AuthChildGuard],
                children: [
                    {
                        path: 'chart-order',
                        loadComponent: () =>
                            import(
                                './pages/statistic-management/chart-order/chart-order.component'
                            ).then((v) => v.ChartOrderComponent),
                        data: { 
                            title: 'sidebar.chartOrder',
                            controller: 'Order',
                            actionName: 'Order-GetOrderPaymentChart',
                        },
                    },
                    {
                        path: 'chart-overview',
                        loadComponent: () =>
                            import(
                                './pages/statistic-management/chart-overview/chart-overview.component'
                            ).then((v) => v.ChartOverviewComponent),
                        data: { 
                            title: 'sidebar.chartOverview',
                            controller: 'Order',
                            actionName: 'Order-StatisTicGetOrderChart7Day',
                        },
                    },
                ],
            },
            {
                path: 'system-setting',
                data: { title: 'sidebar.systemSetting' },
                component: SystemSettingComponent,
                canActivate: [authGuard],
                canActivateChild: [AuthChildGuard],
                children: [
                    {
                        path: 'cheep-management',
                        loadComponent: () =>
                            import(
                                './pages/system-setting/cheep-flight-management/cheep-flight-management.component'
                            ).then((v) => v.CheepFlightManagementComponent),
                        data: { 
                            title: 'pages.cheepFlight.titlePage',
                            controller: 'CheepFlight',
                            actionName: 'ManageCheepFlight',
                        },
                    },
                    {
                        path: 'location-management',
                        loadComponent: () =>
                            import(
                                './pages/system-setting/location-management/location-management.component'
                            ).then((v) => v.LocationManagementComponent),
                        data: { 
                            title: 'pages.locationManagement.titlePage',
                            controller: 'LocationInfo',
                            actionName: 'LocationInfoManage',
                        },
                    },
                    {
                        path: 'favorite-location-management',
                        loadComponent: () =>
                            import(
                                './pages/system-setting/favorite-location-management/favorite-location-management.component'
                            ).then((v) => v.FavoriteLocationManagementComponent),
                        data: { 
                            title: 'pages.favoriteLocationManagement.titlePage',
                            controller: 'FavoriteLocation',
                            actionName: 'FavoriteLocationManage',
                        },
                    },
                    {
                        path: 'partner',
                        loadComponent: () =>
                            import(
                                './pages/system-setting/partner-management/partner-management.component'
                            ).then((v) => v.PartnerManagementComponent),
                        data: { 
                            title: 'sidebar.partnerManagement',
                            controller: 'PartnerInfo',
                            actionName: 'PartnerInfoManage',
                        },
                    },
                    {
                        path: 'service-fee',
                        loadComponent: () =>
                            import(
                                './pages/system-setting/service-fee-management/service-fee-management.component'
                            ).then((v) => v.ServiceFeeManagementComponent),
                        data: { 
                            title: 'sidebar.settingServiceFeeManagement',
                            controller: 'ServiceFee',
                            actionName: 'ManageServiceFee',
                        },
                    },
                    {
                        path: 'service-fee-create-update',
                        loadComponent: () =>
                            import(
                                './pages/system-setting/service-fee-management/create-update-service-fee-config/create-update-service-fee-config.component'
                            ).then((v) => v.CreateUpdateServiceFeeConfigComponent),
                        data: { 
                            title: 'sidebar.settingServiceFeeManagement',
                            controller: 'ServiceFee',
                            actionName: 'ManageServiceFee',
                        },
                    },
                    {
                        path: 'setting-management',
                        loadComponent: () =>
                            import('./pages/system-setting/setting-management/setting-management.component').then(
                                (v) => v.SettingManagementComponent
                            ),
                        data: { 
                            title: 'sidebar.settingManagement',
                            controller: 'Setting',
                            actionName: 'ManageSetting',
                        },
                    },
                ],
            },
            {
                path: 'system-log',
                loadComponent: () =>
                    import('./pages/system-log/system-log.component').then(
                        (v) => v.SystemLogComponent
                    ),
                title: 'Log hệ thống',
                children: [
                    {
                        path: 'booking',
                        data: { 
                            title: 'Booking log',
                            controller: 'LogSystem',
                            actionName: 'BookingLog',
                        },
                        loadComponent: () =>
                            import('./pages/system-log/booking-log/booking-log.component').then(
                                (c) => c.BookingLogComponent
                            ),
                    },
                ],
            },

            {
                path: 'carrier-api/:code',
                loadComponent: () =>
                    import(
                        './pages/api-carrier-management/api-carrier-management.component'
                    ).then((v) => v.ApiCarrierManagementComponent),
                data: { title: 'sidebar.apiCarrierManagement' },
            },
            {
                path: 'error/403-access-denied',
                component: AccessDeniedComponent,
                data: { title: 'common.action.noDataFound' },
            },
            {
                path: 'error/404-not-found',
                component: NotFoundComponent,
                data: { title: 'common.pageNotFound' },
            },
            { path: '', redirectTo: '/booking/booking-manage', pathMatch: 'full' },
        ],
    },
    {
        path: 'auth',
        data: { title: 'pages.auth.login' },
        loadComponent: () => import('./pages/auth/auth.component').then((v) => v.AuthComponent),
        children: [
            {
                path: 'login',
                loadComponent: () =>
                    import('./pages/auth/sign-in/sign-in.component').then((v) => v.SignInComponent),
                data: { title: 'pages.auth.login' },
            },
        ],
    },
    { path: '', redirectTo: '/booking/booking-manage', pathMatch: 'full' },
    { path: '**', redirectTo: '/error/404-not-found' },
];
